import React from "react";
import { motion } from "framer-motion";
import { Store, MapPin, Star, ShieldCheck, Search, Send } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState, SkeletonRows } from "@/components/dash/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";

export default function VendorMarketplace() {
  const [vendors, setVendors] = React.useState(null);
  const [q, setQ] = React.useState("");
  const [detail, setDetail] = React.useState(null);

  const load = React.useCallback(async () => {
    setVendors(null);
    try { const { data } = await api.get("/organizations", { params: { type: "vendor", q, page_size: 24 } }); setVendors(data.items); }
    catch { setVendors([]); }
  }, [q]);
  React.useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <PageHeader title="Vendor Network" description="Discover and evaluate verified vendors across services and technologies." breadcrumb="Vendors / Network" />
      <div className="relative max-w-sm mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendors, services, tech…" className="pl-9" data-testid="marketplace-search" />
      </div>
      {vendors === null ? <SkeletonRows rows={6} /> : vendors.length === 0 ? (
        <EmptyState icon="Store" title="No vendors found" description="Try adjusting your search." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {vendors.map((v, i) => (
            <motion.div key={v.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="vs-card vs-card-hover p-5" data-testid={`vendor-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-xl vs-gradient flex items-center justify-center text-white font-display font-bold">{v.name[0]}</div>
                <StatusBadge status={v.verification_status} />
              </div>
              <h3 className="font-display font-semibold text-slate-900 mt-3">{v.name}</h3>
              <div className="flex items-center gap-1 text-xs text-slate-500 mt-1"><MapPin className="h-3 w-3" />{v.location || "—"}</div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(v.technologies || []).slice(0, 4).map((t) => <span key={t} className="text-[11px] rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{t}</span>)}
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                <span className="flex items-center gap-1 text-sm font-semibold text-amber-500"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />{v.rating || "—"}</span>
                <Button size="sm" variant="outline" onClick={() => setDetail(v)} data-testid={`vendor-view-${v.id}`}>View Profile</Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="bg-white w-full sm:max-w-md overflow-y-auto">
          {detail && <>
            <SheetHeader><SheetTitle className="font-display">{detail.name}</SheetTitle></SheetHeader>
            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /><StatusBadge status={detail.verification_status} /></div>
              <Row k="Industry" v={detail.industry} /><Row k="Location" v={detail.location} /><Row k="Company Size" v={detail.size} />
              <Row k="Services" v={(detail.services || []).join(", ")} /><Row k="Technologies" v={(detail.technologies || []).join(", ")} />
              <Row k="GST" v={detail.gst} /><Row k="PAN" v={detail.pan} /><Row k="Website" v={detail.website} />
              <Row k="About" v={detail.description} />
              <div className="flex gap-2 pt-4">
                <Button className="flex-1 bg-vs-royal text-white" onClick={() => { toast.success(`Invitation sent to ${detail.name}`); }} data-testid="vendor-invite"><Send className="h-4 w-4 mr-1.5" />Invite to RFQ</Button>
              </div>
            </div>
          </>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
const Row = ({ k, v }) => (<div><div className="text-xs uppercase tracking-wide text-slate-400 mb-1">{k}</div><div className="text-sm text-slate-800">{v || "—"}</div></div>);
